import { computed, type IComputedValue, toJS } from "mobx"

import { splitCommitMessage } from "@/lib/displayUtils.ts"
import { Model } from "@/lib/Model.ts"

import type { AiAgentTaskHistoryItem, CPrincipal, GitHubPrInfo } from "@/lib/client/generated/TypeDefinitions.ts"
import type { TaskViewModel } from "@/routes/tasks/TaskViewScene/TaskViewModel.ts"


export class TaskVersionsModel extends Model {
  readonly versions: IComputedValue<Version[]>

  constructor(private readonly taskViewModel: TaskViewModel, readonly onCreatePr: () => void) {
    super("TaskVersionsModel")

    this.versions = computed(this.computeVersions)
  }

  private readonly computeVersions = () => {
    const historyItems = this.taskViewModel.historyItems.get()

    const versions = toJS(historyItems).slice(0).sort((a, b) => {
      return b.number - a.number
    })

    return versions.map((version, index) => {
      const [title, remaining] = splitCommitMessage(version.commit.message)

      const isCurrent = this.isVersionCurrent(version, index, versions)
      const checkpoint = version.checkpoint

      let createdBy: CPrincipal | null = null
      if (checkpoint && checkpoint.createdBy) {
        createdBy = checkpoint.createdBy
      }

      const gitHubVersionInfo = this.taskViewModel.gitHubModel.gitHubVersionInfo(this.onCreatePr).get()

      return {
        number: version.number,
        commitId: version.commit.id,
        isCurrent: isCurrent,
        isSuccessful: Boolean(version.isSuccessful),

        title: title,
        subtitle: checkpoint?.subtitle || remaining,
        reasoning: checkpoint?.reasoning,
        createdAt: version.commit.commitDate,
        createdBy: createdBy,

        gitHubVersionInfo: gitHubVersionInfo
      }
    })
  }

  private readonly isVersionCurrent = (version: AiAgentTaskHistoryItem, index: number, versions: AiAgentTaskHistoryItem[]) => {
    if (!version.isActive) {
      return false
    }

    if (index === 0) {
      return true
    }

    return index > 0 && !versions[index - 1].isActive
  }
}

export type Version = {
  number: number
  commitId: string
  isCurrent: boolean
  isSuccessful: boolean

  title: string
  subtitle?: string | null
  reasoning?: string | null
  isGenerating?: boolean

  createdAt: number
  createdBy: CPrincipal | null

  gitHubVersionInfo: GitHubVersionInfo | null
}

export type GitHubVersionInfo = {
  canOpenPr: boolean
  existingPrs: GitHubPrInfo[] | undefined
  onCreatePr: () => void
}